import { useState } from 'react'
import type { CheckInMark } from '../hooks/useCheckIns'
import invariant from 'tiny-invariant'
import { Heart } from 'lucide-react'
import { cn, getGradientPair } from '../lib/utils'
import { totemBorderClass, totemBorderVars } from '../lib/totemBorder'
import { Skeleton } from './ui/skeleton'
import { useForceLoading } from '../lib/skeletonDebug'
import { useLongPress } from '../hooks/useLongPress'
import type { FacepileTotem } from './Facepile'
import type { PartialRange, UserProfile, InterestStatus, TotemBorderStyle } from '../types'
import { CardBackdrop, CardFooter, CardHeader, type ActiveWindow, type Compact } from './InterestCardParts'
import { clickableProps } from '../lib/a11y'
import { focalPosition, type FocalAnchor } from '../lib/focalPosition'

export type CardStatus = 'none' | InterestStatus

export function InterestCard({
  loading: loadingProp,
  ref,
  heartRef,
  imageUrl,
  imageFocal,
  imageUrls,
  imageFocals,
  seed,
  status,
  interestedMemberIds,
  goingMemberIds,
  userProfiles = {},
  currentUserId,
  onToggle,
  onNameClick,
  onOpenDetail,
  onLongPress,
  totemBorder,
  totemFacepile,
  memberRanges,
  memberCheckIns,
  badge,
  nameContent,
  style,
  className,
  compact,
  nameLines,
  crowdMax,
  activeWindow,
  dimContent = false,
  name,
  tagline,
  subtitle,
  updated = false,
  followHint = false,
  historyKey,
}: {
  loading?: boolean
  ref?: React.Ref<HTMLDivElement>
  heartRef?: React.Ref<HTMLElement>
  imageUrl?: string
  imageFocal?: FocalAnchor
  imageUrls?: string[]
  imageFocals?: FocalAnchor[]
  seed?: string
  status?: CardStatus
  interestedMemberIds?: string[]
  goingMemberIds?: string[]
  userProfiles?: Record<string, UserProfile>
  currentUserId?: string | null
  onToggle?: () => void
  onNameClick?: () => void
  onOpenDetail?: (tab: 'people' | 'feed') => void
  onLongPress?: () => void
  totemBorder?: TotemBorderStyle
  totemFacepile?: FacepileTotem[]
  memberRanges?: Record<string, PartialRange>
  /** Festival mode: members checked in here, or checked in at another set. */
  memberCheckIns?: Record<string, CheckInMark>
  badge?: React.ReactNode
  nameContent?: React.ReactNode
  style?: React.CSSProperties
  compact?: 'xs' | 'sm' | 'md'
  /** Lines a compact (sm/md) card's name may wrap to before it's cut off (default 1). */
  nameLines?: number
  crowdMax?: number
  /**
   * The slice of the card the viewer will actually catch, as fractions of its
   * height. The card keeps its normal layout; the rest goes grayscale and the
   * status border moves in onto the slice.
   */
  activeWindow?: ActiveWindow
  /** Fade the heart and facepile row and let taps fall through to the card, e.g. while an overlay control is active. The name and time stay legible. */
  dimContent?: boolean
  className?: string
  name?: string
  /** A secondary line under the name — a set's showcase title. Dropped on compact cards. */
  tagline?: string
  subtitle?: string
  /** Shows the Updated chip (a schedule set that moved since the user last looked). */
  updated?: boolean
  /** A rose-outlined (unfilled) heart: an artist the viewer follows but hasn't hearted for this event. */
  followHint?: boolean
  /** Unique on the page — keeps the member dialog open across a profile visit + swipe back. */
  historyKey?: string
}) {
  const [tapping, setTapping] = useState(false)
  const loading = useForceLoading(loadingProp)
  const longPress = useLongPress(onLongPress)

  if (loading) return <LoadingCard className={className} style={style} />

  invariant(status !== undefined, 'InterestCard: status is required when not loading')
  invariant(interestedMemberIds, 'InterestCard: interestedMemberIds is required when not loading')
  invariant(goingMemberIds, 'InterestCard: goingMemberIds is required when not loading')
  invariant(onToggle, 'InterestCard: onToggle is required when not loading')
  const multiImageUrls = imageUrls && imageUrls.length > 1 ? imageUrls : null
  const hasBackground = !!multiImageUrls || !!imageUrl || !!seed
  const totalMembers = interestedMemberIds.length + goingMemberIds.length
  const { crowdBlue, crowdBorderStyle } = crowdBorder(crowdMax, status, totemBorder, interestedMemberIds, goingMemberIds)
  const isSelfInterest = status === 'interested' || status === 'going'
  const hasFriendInterest = !isSelfInterest && totalMembers > 0
  const imgOverlay = isSelfInterest ? 0.5 : hasFriendInterest ? 0.65 : 0.75
  const noImgOverlay = isSelfInterest ? 0.65 : hasFriendInterest ? 0.75 : 0.85
  const layeredBackground = !!multiImageUrls || !!activeWindow
  const bgStyle = layeredBackground ? {} : flatBackground(imageUrl, imageFocal, seed, imgOverlay, noImgOverlay)
  const openMembers = onOpenDetail ? () => onOpenDetail('people') : undefined
  const toggle = () => {
    setTapping(true)
    onToggle()
  }

  return (
    <div
      ref={ref}
      data-testid="interest-card"
      style={{ ...bgStyle, ...crowdBorderStyle, ...(totemBorder ? totemBorderVars(totemBorder) : {}), ...style }}
      className={cn(
        '@container relative isolate rounded border cursor-pointer flex flex-col justify-start',
        !totemBorder && 'overflow-hidden',
        'select-none [-webkit-touch-callout:none]',
        outerPadClass(compact, !!activeWindow),
        !compact ? 'min-h-[80px]' : '',
        'transition duration-200 ease-out',
        'hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 hover:z-30',
        tapping && 'animate-card-tap',
        totemBorder && totemBorderClass(totemBorder),
        frameClass(totalMembers > 0, hasBackground, crowdBlue),
        !activeWindow && status === 'interested' && 'border-2 border-interested hover:border-interested/80',
        !activeWindow && status === 'going' && 'border-2 border-going hover:border-going/80',
        activeWindow && 'border-border hover:border-border',
        className,
      )}
      {...clickableProps(e => {
        if (!(e.currentTarget as HTMLElement).contains(e.target as Node)) return
        if (onNameClick) {
          onNameClick()
        } else {
          toggle()
        }
      })}
      onAnimationEnd={() => setTapping(false)}
      {...longPress}
    >
      <CardBackdrop
        activeWindow={activeWindow}
        layered={layeredBackground}
        imageUrl={imageUrl}
        imageFocal={imageFocal}
        imageUrls={imageUrls}
        imageFocals={imageFocals}
        seed={seed}
        status={status}
        imgOverlay={imgOverlay}
        noImgOverlay={noImgOverlay}
      />
      <CardHeader
        compact={compact}
        nameLines={nameLines}
        dimContent={dimContent}
        heartRef={heartRef}
        status={status}
        hasBackground={hasBackground}
        onToggle={toggle}
        nameContent={nameContent}
        name={name}
        tagline={tagline}
        subtitle={subtitle}
        updated={updated}
        followHint={followHint}
      />
      {!compact && (
        <CardFooter
          dimContent={dimContent}
          badge={badge}
          interestedMemberIds={interestedMemberIds}
          goingMemberIds={goingMemberIds}
          profiles={userProfiles}
          currentUserId={currentUserId}
          currentUserStatus={status === 'none' ? null : status}
          totems={totemFacepile}
          memberRanges={memberRanges}
          memberCheckIns={memberCheckIns}
          dialogTitle={name}
          dialogSubtitle={subtitle}
          historyKey={historyKey}
          onClick={openMembers}
        />
      )}
    </div>
  )
}

function LoadingCard({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <div
      className={cn(
        'relative rounded border overflow-hidden flex flex-col justify-start min-h-[80px] bg-muted',
        className,
      )}
      style={style}
    >
      <div className="absolute top-0 right-0 z-40 p-1">
        <Heart className="size-5 text-muted-foreground/40" />
      </div>
      <div className="p-2 pb-1 flex-1 flex flex-col gap-0.5">
        <Skeleton className="h-4 w-3/4 pr-9" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <div className="h-7 px-1.5" />
    </div>
  )
}

// A marked card's status border is 2px; with an activeWindow that border moves
// onto the window and the card's own drops to 1px, so pad the missing pixel
// back or the content shifts when a range is set or cleared.
// md and full cards hand 4px of their top padding to the header (`pt-1` in
// CardHeader), where the heart can ride up into it without being clipped.
function outerPadClass(compact: Compact, hasWindow: boolean): string {
  return hasWindow
    ? compact === 'xs' ? 'px-[7px] py-[2px]' : compact === 'sm' ? 'px-[7px] py-[3px]' : 'px-[7px] pb-[7px] pt-[3px]'
    : compact === 'xs' ? 'px-1.5 py-px' : compact === 'sm' ? 'px-1.5 py-0.5' : 'px-1.5 pb-1.5 pt-0.5'
}

// Graduated "blue" border: thicker and brighter the more pull a set has, scaled
// *proportionally* to the busiest set (crowdMax) so the top set always hits the
// ceiling and the rest scale relative to it. Pull is a weighted score — "going"
// is worth 3, "interested" 1 — so commitment counts more than a maybe. Only
// applies to un-marked cards (status === 'none') without a totem border;
// interested/going override with a rose/emerald border further down.
function crowdBorder(
  crowdMax: number | undefined,
  status: CardStatus,
  totemBorder: TotemBorderStyle | undefined,
  interestedMemberIds: string[],
  goingMemberIds: string[],
): { crowdBlue: boolean; crowdBorderStyle: React.CSSProperties } {
  const crowdScore = goingMemberIds.length * 3 + interestedMemberIds.length
  const crowdBlue =
    !!crowdMax && crowdMax > 0 && status === 'none' && !totemBorder && crowdScore > 0
  const crowdIntensity = crowdBlue ? Math.min(crowdScore / crowdMax!, 1) : 0
  const crowdBorderStyle: React.CSSProperties = crowdBlue
    ? {
        borderWidth: `${(0.9 + crowdIntensity * 1.4).toFixed(2)}px`,
        borderColor: `oklch(from var(--primary) calc(l + ${(crowdIntensity * 0.13).toFixed(3)}) c h)`,
      }
    : {}
  return { crowdBlue, crowdBorderStyle }
}

function flatBackground(
  imageUrl: string | undefined,
  focal: FocalAnchor,
  seed: string | undefined,
  imgOverlay: number,
  noImgOverlay: number,
): React.CSSProperties {
  if (imageUrl) {
    return {
      backgroundImage: `linear-gradient(rgba(0,0,0,${imgOverlay}), rgba(0,0,0,${imgOverlay})), url(${imageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: focalPosition(focal, 'center top'),
    }
  }
  if (seed) {
    const [c1, c2] = getGradientPair(seed)
    return {
      backgroundImage: `linear-gradient(rgba(0,0,0,${noImgOverlay}), rgba(0,0,0,${noImgOverlay})), linear-gradient(135deg, ${c1}, ${c2})`,
    }
  }
  return {}
}


function frameClass(hasMembers: boolean, hasBackground: boolean, crowdBlue: boolean): string {
  return hasMembers
    ? hasBackground
      ? crowdBlue ? '' : 'border-primary'
      : cn('bg-primary/15 hover:bg-primary/25', !crowdBlue && 'border-primary')
    : hasBackground
      ? 'border-primary/20 hover:border-primary/30'
      : 'bg-primary/5 border-primary/20 hover:bg-primary/10'
}

